import { useCallback, useEffect, useMemo, useState } from 'react';
import api, { getImageUrl } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Search, Users, MapPin, FolderOpen, CheckSquare, GitMerge, Copy, Link2, Plus } from 'lucide-react';

// Shared grid for the workspace-wide Characters / Locations areas
export function WorkspaceGrid({ kind, items, projects, loading, renderMeta, imageOf, extraFilters, filterFn, onOpen, onFindDuplicates, onMergeSelected, onCreate }) {
  const [q, setQ] = useState('');
  const [projectId, setProjectId] = useState('all');
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const Icon = kind === 'character' ? Users : MapPin;
  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    const filtered = items.filter(x => (projectId === 'all' || x.project_id === projectId)
      && (!s || (x.name || '').toLowerCase().includes(s) || (x.description || '').toLowerCase().includes(s) || (x.project_name || '').toLowerCase().includes(s))
      && (!filterFn || filterFn(x)));
    // Merged identities (same sync_group) collapse into one card, represented by the primary
    const groups = new Map();
    for (const x of filtered) {
      const key = x.sync_group || x.id;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(x);
    }
    return [...groups.values()].map(members => {
      const rep = members.find(m => m.id === m.sync_group) || members[0];
      return members.length > 1 ? { ...rep, identity_members: members } : rep;
    });
  }, [items, q, projectId, filterFn]);

  const toggleSelect = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const exitSelect = () => { setSelectMode(false); setSelected([]); };

  return (
    <div data-testid={`workspace-${kind}s-page`}>
      <div className="flex items-center gap-2 flex-wrap mb-5">
        <select data-testid="workspace-project-filter" value={projectId} onChange={e => setProjectId(e.target.value)} className="ff-input text-xs py-2">
          <option value="all">All projects ({items.length})</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name} ({items.filter(x => x.project_id === p.id).length})</option>)}
        </select>
        {extraFilters}
        <div className="relative ml-auto">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#55555C]" />
          <input data-testid="workspace-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" className="ff-input pl-8 text-xs py-2 w-48" />
        </div>
        {onFindDuplicates && (
          <button data-testid="workspace-find-duplicates" onClick={onFindDuplicates} disabled={items.length < 2}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-white/[0.03] text-[#c8c8d0] border border-white/[0.06] hover:text-white hover:bg-white/[0.06] disabled:opacity-40 transition-all">
            <Copy className="w-3.5 h-3.5" /> Find duplicates
          </button>
        )}
        {onMergeSelected && (
          <button data-testid="workspace-select-toggle" onClick={() => selectMode ? exitSelect() : setSelectMode(true)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-all ${selectMode ? 'bg-[#A78BFA]/20 text-[#A78BFA] border-[#A78BFA]/40' : 'bg-white/[0.03] text-[#8A8A93] border-white/[0.06] hover:text-white'}`}>
            <CheckSquare className="w-3.5 h-3.5" /> {selectMode ? 'Done' : 'Select'}
          </button>
        )}
        {onCreate && (
          <button data-testid={`workspace-new-${kind}`} onClick={onCreate} className="ff-btn-primary flex items-center gap-1.5 text-xs">
            <Plus className="w-3.5 h-3.5" /> New {kind}
          </button>
        )}
      </div>
      {selectMode && (
        <div className="flex flex-wrap items-center gap-3 mb-5 p-3 rounded-xl bg-[#A78BFA]/10 border border-[#A78BFA]/25" data-testid="workspace-select-bar">
          <span className="text-xs text-[#C4B5FD] font-semibold">{selected.length} selected</span>
          <button data-testid="workspace-select-clear" onClick={() => setSelected([])} className="text-xs text-[#8A8A93] hover:text-white">Clear</button>
          <button data-testid="workspace-merge-selected" disabled={selected.length < 2}
            onClick={() => { onMergeSelected(items.filter(x => selected.includes(x.id))); }}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#FF4500] text-white hover:bg-[#E03C00] disabled:opacity-40">
            <GitMerge className="w-3.5 h-3.5" /> Merge {selected.length > 1 ? selected.length : ''} into one
          </button>
        </div>
      )}
      {loading ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#8A8A93]" /></div>
        : visible.length === 0 ? (
          <div className="ff-card p-12 text-center">
            <Icon className="w-10 h-10 text-[#55555C] mx-auto mb-3 opacity-40" />
            <p className="text-sm text-[#8A8A93]">{items.length === 0 ? `No ${kind}s in this workspace yet — they appear here as you build projects.` : 'Nothing matches your filters.'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {visible.map(x => {
              const on = selected.includes(x.id);
              return (
                <button key={x.id} data-testid={`workspace-item-${x.id}`} onClick={() => selectMode ? toggleSelect(x.id) : onOpen(x)}
                  className={`relative text-left rounded-xl overflow-hidden border bg-[#141418] hover:-translate-y-0.5 transition-all ${on ? 'border-[#A78BFA] ring-2 ring-[#A78BFA]/40' : 'border-white/[0.06] hover:border-[#FF4500]/50'}`}>
                  {selectMode && (
                    <span data-testid={`workspace-select-${x.id}`} className={`absolute top-2 left-2 z-10 w-5 h-5 rounded-md border flex items-center justify-center ${on ? 'bg-[#A78BFA] border-[#A78BFA]' : 'bg-black/60 border-white/40'}`}>
                      {on && <CheckSquare className="w-3.5 h-3.5 text-white" />}
                    </span>
                  )}
                  <div className={`${kind === 'character' ? 'aspect-square' : 'aspect-video'} bg-[#0a0a0e] relative`}>
                    {imageOf(x) ? <img src={getImageUrl(imageOf(x))} alt={x.name} className="w-full h-full object-cover" /> : <Icon className="w-8 h-8 text-[#55555C] absolute inset-0 m-auto" />}
                  </div>
                  <div className="p-3">
                    <p className="text-sm text-white font-semibold truncate">{x.name}</p>
                    {x.identity_members ? (
                      <p className="text-[10px] text-[#10B981] truncate flex items-center gap-1 mt-0.5" data-testid={`workspace-identity-${x.id}`} title={x.identity_members.map(m => m.project_name).join(', ')}>
                        <Link2 className="w-3 h-3" /> One identity in {x.identity_members.length} projects
                      </p>
                    ) : (
                      <p className="text-[10px] text-[#8A8A93] truncate flex items-center gap-1 mt-0.5"><FolderOpen className="w-3 h-3" /> {x.project_name}</p>
                    )}
                    {renderMeta?.(x)}
                  </div>
                </button>
              );
            })}
          </div>
        )}
    </div>
  );
}

export function useWorkspaceList(endpoint, key) {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(() => {
    return api.get(endpoint).then(r => { setItems(r.data[key] || []); setProjects(r.data.projects || []); })
      .catch(() => toast.error('Failed to load workspace data')).finally(() => setLoading(false));
  }, [endpoint, key]);
  useEffect(() => { reload(); }, [reload]);
  return { items, projects, loading, setItems, reload };
}
